import React, { useMemo, useState, useEffect } from 'react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { rectSwappingStrategy, SortableContext } from '@dnd-kit/sortable'
import { useAppDispatch } from '../../../../../../../../../../../app/hooks'
import { changeImageOrder } from '../../../../../../../../../../../redux/thunks/multimedia/multimedia'
import { AttachmentData } from '../../../../../../../../../../../@types/types'
import { MultimediaUploaderType } from '../../../../../../../../../../../@types/multimedia-types'
import { convertImageUrl } from '../../../../../../../../../../../services/conversion/conversionServices'
import { AddPictureButton } from '../addPictureButton/AddPictureButton'
import { SortablePictureItem } from '../sortablePictureItem/SortablePictureItem'
import { calculateDragPreviewSize, getInitialDragPreviewSize } from './dragPreviewUtils'
import styles from './DraggablePicturesGrid.module.css'

export interface DraggablePicturesGridProps {
  attachments: AttachmentData[]
  selectedIds: string[]
  onPictureSelect: (attachmentId: string) => void
  flowInformation?: any
  allowedFileType?: MultimediaUploaderType
  entryMode?: string
}

export const DraggablePicturesGrid: React.FC<DraggablePicturesGridProps> = ({
  attachments,
  selectedIds,
  onPictureSelect,
  flowInformation,
  allowedFileType,
  entryMode,
}) => {
  const dispatch = useAppDispatch()
  const [activeId, setActiveId] = useState<UniqueIdentifier>()
  const [dragPreviewSize, setDragPreviewSize] = useState<number>(getInitialDragPreviewSize)

  // Calculate drag preview size based on screen width and grid item size
  useEffect(() => {
    const handleResize = () => {
      setDragPreviewSize(calculateDragPreviewSize(window.innerWidth))
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event

    if (over && active.id !== over.id) {
      dispatch(changeImageOrder(active.id, over.id))
    }

    setActiveId(undefined)
  }

  const activeAttachment = useMemo(
    () =>
      activeId !== undefined ? attachments.find((item) => (item.attachment?.id || item.file?.id) === activeId) : null,
    [attachments, activeId]
  )

  // Use distance-based activation like the working PictureList implementation
  const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 10 } })

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 300,
      tolerance: 5,
    },
  })

  const sensors = useSensors(mouseSensor, touchSensor, useSensor(KeyboardSensor))
  const items = attachments.map((attachment) => attachment.attachment?.id || attachment.file?.id || '')

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => setActiveId(event.active.id)}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
      data-testid='draggable-pictures-grid'
    >
      <div className={`${styles.grid} gap-m margin-bottom-l`}>
        <AddPictureButton flowInformation={flowInformation} allowedFileType={allowedFileType} entryMode={entryMode} />

        <SortableContext items={items} strategy={rectSwappingStrategy}>
          {attachments.map((attachment, index) => (
            <SortablePictureItem
              key={attachment.attachment?.id || attachment.file?.id || index}
              data={attachment}
              index={index}
              isSelected={selectedIds.includes(attachment.attachment?.id || attachment.file?.id || '')}
              onClick={() => onPictureSelect(attachment.attachment?.id || attachment.file?.id || '')}
            />
          ))}
        </SortableContext>
      </div>

      <DragOverlay>
        {activeAttachment && (
          <div
            className={styles.dragOverlay}
            style={{
              width: `${dragPreviewSize}px`,
              height: `${dragPreviewSize}px`,
            }}
          >
            <div
              className={styles.dragOverlayImage}
              style={{
                backgroundImage: `url(${convertImageUrl('400', '400', activeAttachment.attachment?.url || '')})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
